import React, { useMemo } from "react";
import { EditableFieldWrapper } from "@adsk/acc-issues-common/components/primitive/EditableFieldWrapper";
import { isAttributeEditable } from "../../utils/data/entityPermissions";
import { DefaultEditFieldInterface } from "../../IssueDetailPanel/IssueDetailTabPanel/detail-panel-fields/typesForFields";
import { useTranslations } from "@adsk/acc-issues-translations";
import {
  LocationPicker,
  LocationNameDisplay,
  useLocations,
  LocationNode,
} from "@adsk/acc-locations-components";
import { useIsLocationInTree } from "../../utils/hooks/locationsHooks";

const locationRenderer = (locationId: string) => (
  <LocationNameDisplay locationNodeId={locationId} />
);

// This Field is similar to the one in IssuesTab/TemplatesConfig/IssueTemplateDetailPanel/detail-panel-fields/...
// In case any necessary change is needed, it would be nice check on the other file as well
export const LocationPickerField = ({
  issue,
  onChange,
  containerRef,
  required = false,
  viewOnly = false,
}: DefaultEditFieldInterface) => {
  const { T } = useTranslations();
  const disabled = viewOnly || !isAttributeEditable(issue, "locationId");
  const { isLoading } = useLocations();
  const isLocationInTree = useIsLocationInTree();
  const locationExists = useMemo(
    () => isLocationInTree(issue.locationId),
    [issue, isLocationInTree]
  );

  return (
    <div data-testid="location-field">
      <EditableFieldWrapper
        fieldKey="locationId"
        label={T.translate("issueAttributes.location")}
        value={locationExists ? issue.locationId : undefined}
        disabled={disabled}
        containerRef={containerRef}
        loading={isLoading}
        showDisabledReason={!viewOnly}
        renderInput={({ onChange: inlineEditOnChange }) => (
          <LocationPicker
            onClose={() => {
              /** When the LocationPicker's onClickOutside calls onClose, we need to call the InlineEdit's onChange function (inlineEditOnChange)
               *  in order to set the InlineEdit's edit mode to closed
               */
              inlineEditOnChange(issue.locationId);
            }}
            hideIncludeSubLocations
            clearable={!required}
            isOpen
            selectedNodeId={issue.locationId}
            onNodeSelect={location => {
              if (!location) {
                // we only want to clear the field if it is not required
                if (!required) {
                  inlineEditOnChange(undefined);
                  onChange({
                    locationId: null,
                  });
                }
              } else {
                inlineEditOnChange(location);
                onChange({
                  locationId: (location as LocationNode).id,
                });
              }
            }}
          />
        )}
        renderValue={locationExists ? locationRenderer : undefined}
        required={required}
      />
    </div>
  );
};
